import React, { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import "./AdminDashboard.css";

const API = process.env.REACT_APP_BACKEND_URL;

const BADGE_COLORS = {
  "Beat & Vocal": ["rgba(124,58,237,0.2)", "#a78bfa"],
  "Lyrics": ["rgba(6,182,212,0.15)", "#22d3ee"],
  "Rhyme & Flow": ["rgba(255,0,102,0.15)", "#ff4d94"],
  "Voice Generation": ["rgba(204,255,0,0.12)", "#ccff00"],
  "Stem Separation": ["rgba(249,115,22,0.15)", "#fb923c"],
  "MANNIS Analysis": ["rgba(6,182,212,0.15)", "#22d3ee"],
  "MIDI Download": ["rgba(204,255,0,0.12)", "#ccff00"],
  "Suno Song": ["rgba(124,58,237,0.2)", "#a78bfa"],
  "Checkout": ["rgba(255,0,102,0.2)", "#ff0066"],
  "New Signup": ["rgba(34,197,94,0.15)", "#4ade80"],
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const then = new Date(iso.slice(0, 19) + "Z").getTime();
  if (isNaN(then)) return iso.slice(0, 10);
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const authHeaders = () => {
  const token = localStorage.getItem("sounddna_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function LoginGate({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      if (data.role !== "admin") throw new Error("This account does not have admin access");
      localStorage.setItem("sounddna_token", data.access_token);
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="adm-login-wrap">
      <form className="adm-login-card" onSubmit={submit} data-testid="admin-login-form">
        <h1>SOUND <span>DNA</span> PRO</h1>
        <p>Mission Control — Admin access only</p>
        <input className="adm-input" type="email" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" />
        <input className="adm-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" />
        <button className="adm-login-btn" type="submit" disabled={busy} data-testid="admin-login-submit">
          {busy ? "Signing in…" : "Enter Mission Control"}
        </button>
        {error && <div className="adm-login-error" data-testid="admin-login-error">{error}</div>}
      </form>
    </div>
  );
}

function TokenManager() {
  const [email, setEmail] = useState("");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [adjust, setAdjust] = useState("");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");

  const lookup = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true); setError(""); setMsg(""); setInfo(null);
    try {
      const res = await fetch(`${API}/api/admin/user-tokens?email=${encodeURIComponent(email)}`, { headers: authHeaders(), credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Lookup failed");
      setInfo(data);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const applyAdjust = async () => {
    const amt = parseInt(adjust, 10);
    if (!amt || !info) return;
    setBusy(true); setError(""); setMsg("");
    try {
      const res = await fetch(`${API}/api/admin/adjust-tokens`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({ email: info.email, amount: amt, note: note || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Adjust failed");
      setMsg(`Done! ${data.adjusted_by > 0 ? "+" : ""}${data.adjusted_by} tokens → new balance: ${data.new_balance}`);
      setAdjust(""); setNote("");
      const r2 = await fetch(`${API}/api/admin/user-tokens?email=${encodeURIComponent(info.email)}`, { headers: authHeaders(), credentials: "include" });
      if (r2.ok) setInfo(await r2.json());
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="adm-panel" style={{ marginTop: "1.25rem" }} data-testid="admin-token-manager">
      <h2 className="adm-panel-title">⚡ Token Manager</h2>
      <form onSubmit={lookup} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input className="adm-input" style={{ flex: 1, margin: 0 }} type="email" placeholder="Customer email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="token-manager-email-input" />
        <button className="adm-login-btn" style={{ width: "auto", padding: "0 18px", margin: 0 }} type="submit" disabled={busy} data-testid="token-manager-lookup-btn">
          {busy ? "…" : "Lookup"}
        </button>
      </form>
      {error && <div className="adm-login-error" data-testid="token-manager-error">{error}</div>}
      {msg && <div style={{ color: "#4ade80", fontSize: "0.85rem", marginBottom: 10 }} data-testid="token-manager-success">{msg}</div>}
      {info && (
        <div data-testid="token-manager-result">
          <div className="adm-tier-row">
            <span>Extra token balance</span>
            <span className="adm-tier-count" data-testid="token-manager-balance">{info.token_balance}</span>
          </div>
          <div className="adm-tier-row">
            <span>Plan</span>
            <span className="adm-tier-count" style={{ textTransform: "capitalize" }}>{info.subscription}</span>
          </div>
          <div style={{ display: "flex", gap: 8, margin: "12px 0" }}>
            <input className="adm-input" style={{ width: 110, margin: 0 }} type="number" placeholder="+/- amount" value={adjust} onChange={(e) => setAdjust(e.target.value)} data-testid="token-manager-adjust-input" />
            <input className="adm-input" style={{ flex: 1, margin: 0 }} type="text" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="token-manager-note-input" />
            <button className="adm-login-btn" style={{ width: "auto", padding: "0 16px", margin: 0 }} onClick={applyAdjust} disabled={busy || !adjust} data-testid="token-manager-apply-btn">Apply</button>
          </div>
          <h2 className="adm-panel-title" style={{ marginTop: 10 }}>Recent Token Activity</h2>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {(info.ledger || []).length === 0 && <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}>No token activity recorded</div>}
            {(info.ledger || []).map((l, i) => (
              <div className="adm-tier-row" key={i} data-testid={`token-manager-ledger-${i}`}>
                <span style={{ fontSize: "0.8rem" }}>{l.description}<br /><span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.7rem" }}>{(l.created_at || "").slice(0, 16).replace("T", " ")}</span></span>
                <span className="adm-tier-count" style={{ color: l.amount > 0 ? "#4ade80" : "#ff4d94" }}>{l.amount > 0 ? `+${l.amount}` : l.amount}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [authState, setAuthState] = useState("loading"); // loading | denied | ok

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, { headers: authHeaders(), credentials: "include" });
      if (res.status === 401 || res.status === 403) {
        setAuthState("denied");
        return;
      }
      const data = await res.json();
      setStats(data);
      setAuthState("ok");
    } catch (e) {
      console.error("stats load failed", e);
    }
  }, []);

  const loadActivity = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/activity`, { headers: authHeaders(), credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (authState !== "ok") return;
    loadActivity();
    const a = setInterval(loadActivity, 10000);
    const s = setInterval(loadStats, 30000);
    return () => { clearInterval(a); clearInterval(s); };
  }, [authState, loadActivity, loadStats]);

  if (authState === "loading") return <div className="adm-loading">LOADING MISSION CONTROL…</div>;
  if (authState === "denied") return <LoginGate onSuccess={() => { setAuthState("loading"); loadStats(); }} />;
  if (!stats) return <div className="adm-loading">LOADING METRICS…</div>;

  const heroStats = [
    { label: "Total Users", value: stats.users.total, sub: `+${stats.users.new_7d} this week`, accent: "#ccff00" },
    { label: "Paying Users", value: stats.users.paying, sub: `${stats.conversion_rate}% conversion`, accent: "#ff0066" },
    { label: "Active Subscriptions", value: stats.subscriptions_active, sub: `${stats.revenue.transactions} checkouts`, accent: "#7c3aed" },
    { label: "Checkout Volume", value: `$${stats.revenue.checkout_volume.toLocaleString()}`, sub: `$${stats.revenue.paid_total.toLocaleString()} collected`, accent: "#06b6d4" },
    { label: "Total Generations", value: stats.generations.total.toLocaleString(), sub: `+${stats.generations.last_7d} last 7 days`, accent: "#ccff00" },
    { label: "Prompt Library", value: stats.prompt_library.toLocaleString(), sub: `${stats.marketplace_dnas} marketplace DNAs`, accent: "#ff0066" },
  ];

  return (
    <div className="adm-root" data-testid="admin-dashboard">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">SOUND <span>DNA</span> MISSION CONTROL</h1>
          <div className="adm-subtitle">Live Business Metrics — Investor View</div>
        </div>
        <div className="adm-live" data-testid="admin-live-indicator">
          <span className="adm-live-dot" /> LIVE · updates every 10s
        </div>
      </header>

      <div className="adm-hero-grid" data-testid="admin-hero-stats">
        {heroStats.map((s) => (
          <div className="adm-stat-card" key={s.label} style={{ "--card-accent": s.accent }}>
            <div className="adm-stat-value">{s.value}</div>
            <div className="adm-stat-label">{s.label}</div>
            <div className="adm-stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="adm-main-grid">
        <div>
          <div className="adm-panel" data-testid="admin-trend-panel">
            <h2 className="adm-panel-title">Generation Activity — Last 14 Days</h2>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <AreaChart data={stats.trends} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="voltFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ccff00" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#ccff00" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#14141c", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f4f4f5" }} />
                  <Area type="monotone" dataKey="count" stroke="#ccff00" strokeWidth={2} fill="url(#voltFill)" name="Generations" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <h2 className="adm-panel-title" style={{ marginTop: "1.75rem" }}>Feature Usage</h2>
            <div className="adm-feature-grid" data-testid="admin-feature-grid">
              {stats.generations.features.map((f) => (
                <div className="adm-feature-card" key={f.key}>
                  <div className="adm-feature-total">{f.total.toLocaleString()}</div>
                  <div className="adm-feature-label">{f.label}</div>
                  <div className="adm-feature-today">+{f.today} today · +{f.last_7d} / 7d</div>
                </div>
              ))}
            </div>
          </div>

          <div className="adm-panel" style={{ marginTop: "1.25rem" }} data-testid="admin-tiers-panel">
            <h2 className="adm-panel-title">Subscription Tiers</h2>
            {Object.entries(stats.users.tiers).map(([tier, n]) => (
              <div className="adm-tier-row" key={tier}>
                <span style={{ textTransform: "capitalize" }}>{tier.replace("_", " ")}</span>
                <span className="adm-tier-count">{n}</span>
              </div>
            ))}
          </div>

          <TokenManager />
        </div>

        <div className="adm-panel" data-testid="admin-activity-panel">
          <h2 className="adm-panel-title">
            <span className="adm-live-dot" style={{ display: "inline-block", marginRight: 8 }} />
            Live Activity Feed
          </h2>
          <div className="adm-feed" data-testid="admin-activity-feed">
            {events.length === 0 && <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}>No recent activity</div>}
            {events.map((ev, i) => {
              const [bg, fg] = BADGE_COLORS[ev.type] || ["rgba(255,255,255,0.08)", "#f4f4f5"];
              return (
                <div className="adm-feed-item" key={i}>
                  <span className="adm-feed-badge" style={{ background: bg, color: fg }}>{ev.type}</span>
                  <div>
                    {ev.detail && <div className="adm-feed-detail">{ev.detail}</div>}
                    <div className="adm-feed-meta">
                      {ev.email ? `${ev.email} · ` : ""}{timeAgo(ev.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
